import React, { useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { assocPath, isNil, mergeDeepRight, path, pathOr } from '../../lib/ramda'
import { FormHelper } from '../../helpers/FormHelper'
import { Button } from '../Button'
import { ButtonType } from '../Button/ButtonType.constants'
import { SelectionGroup } from '../SelectionGroup'
import { showErrorAlert } from '../../states/alerts/actions'

enum FormSize {
  SM = 'SM',
  MD = 'MD',
  FULL_WIDTH = 'FULL_WIDTH',
}

const FormStyle = `
  display: flex;
  flex-direction: column;
  max-height: 100%;
  width: 100%;
  flex: 1;
`

const StyledForm = styled.form`
  ${FormStyle}
`

const StyledNonForm = styled.div`
  ${FormStyle}
`

const HiddenSubmitButton = styled(Button).attrs({
  type: ButtonType.SUBMIT,
  children: '',
})`
  display: none;
`

const FormContent = styled.div`
  ${({ size }) =>
    size === FormSize.SM &&
    `
    width: 20rem;
    max-width: 90%;
  `}
  ${({ size }) =>
    size === FormSize.MD &&
    `
    width: 30rem;
    max-width: 90%;
  `}
`

const getValidationFields = (items) => {
  let acc = []
  React.Children.forEach(items, (child) => {
    if (!child || !child.props) {
      return
    }
    const { name, validation, required } = child.props
    if (/FormView$/.test(child.type?.displayName)) {
      acc = acc.concat(getValidationFields(child.props.children))
      // return getValidationFields(child.props.children);
    }
    if (!name) {
      return
    }
    acc = acc.concat({
      name,
      validation,
      required,
    })
  })
  return acc
}

interface IFormProps {
  isForm?: boolean
  formFields: object
  className?: string
  showValidationMessages?: boolean
  children: Array<any>
  onSubmit?: (event: any) => void
  onChange: (newFormFields: object, isFormValid?: object) => void
  size?: FormSize
  topChildren?: React.ReactNode
  bottomChildren?: React.ReactNode
}

const Form: React.FC<IFormProps> = ({
  className,
  formFields,
  showValidationMessages = false,
  children,
  onChange,
  onSubmit,
  size = FormSize.FULL_WIDTH,
  topChildren,
  bottomChildren,
  isForm = true,
}) => {
  const dispatch = useDispatch()
  const [hasSubmitClicked, setHasSubmitClicked] = useState(false)
  const validationFields = getValidationFields(children)
  const validationMessages = FormHelper.isFormValid(
    formFields,
    validationFields
  )

  const triggerSubmit = (ev: any) => {
    setHasSubmitClicked(true)
    // console.log('formFields', formFields);
    const vm = FormHelper.isFormValid(formFields, validationFields)
    if (vm && vm.valid) {
      onSubmit && onSubmit(ev)
    } else {
      dispatch(showErrorAlert('Please fill in all the fields correctly.'))
      console.log('form submission failed', vm)
    }
  }

  const renderElements = (items: Array<any>) =>
    React.Children.map(items, (el) => {
      if (!el || !el.props) {
        return null
      }
      const {
        props: { name, onChange: elOnChange },
      } = el
      // if it is a field, it must have a name, otherwise, just render it
      if (/FormView$/.test(el.type?.displayName)) {
        const {
          props: { children: childItems, ...otherProps },
        } = el
        return React.cloneElement(el, {
          children: renderElements(childItems),
          ...otherProps,
        })
      }
      if (!name) {
        return React.cloneElement(el)
      }
      let defaultValue
      if (el.type === SelectionGroup && el.props.multiSelect) {
        defaultValue = []
      }
      const fieldKeys = name.split('.')
      const value = pathOr(defaultValue, fieldKeys, formFields)

      return React.cloneElement(el, {
        validationMessage:
          showValidationMessages || hasSubmitClicked
            ? path(['messages', name], validationMessages)
            : undefined,
        onChange: (ev) => {
          let val
          const type = path(['target', 'type'], ev)
          if (type === 'checkbox') {
            val = ev.target.checked
          } else {
            val = path(['target', 'value'], ev)
            if (isNil(val)) {
              val = ev
            }
          }
          const newFormFields = mergeDeepRight(
            formFields,
            assocPath(fieldKeys, val, {})
          )
          const isFormValid = FormHelper.isFormValid(
            newFormFields,
            validationFields
          )
          onChange(newFormFields, isFormValid)
          if (elOnChange) {
            elOnChange(newFormFields, isFormValid)
          }
        },
        onKeyDown: (ev) => {
          if (ev.key === 'Enter') {
            triggerSubmit(ev)
          }
        },
        value,
        checked: value,
      })
    })

  if (!isForm) {
    return (
      <StyledNonForm>
        {topChildren}
        <FormContent className={className}>
          {renderElements(children)}
        </FormContent>
        {bottomChildren}
      </StyledNonForm>
    )
  }
  return (
    <StyledForm
      size={size}
      onSubmit={(ev) => {
        ev.preventDefault()
        ev.stopPropagation()
        triggerSubmit(ev)
      }}
    >
      {topChildren}
      <FormContent className={className}>
        {renderElements(children)}
      </FormContent>
      {bottomChildren}
      <HiddenSubmitButton />
    </StyledForm>
  )
}

export { Form, FormSize }
